import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, Mail } from "lucide-react";

// Landing page every user of a suspended / canceled / deactivated center is
// redirected to. Fired by the axios interceptor in /src/lib/api.js when the
// backend responds with 403 CENTER_SUSPENDED. Kept intentionally simple:
// no API calls, no auth context, no dependencies on protected routes.
export default function Suspended() {
  const { search } = useLocation();
  const { status, center } = useMemo(() => {
    const p = new URLSearchParams(search);
    return {
      status: (p.get("status") || "suspended").toLowerCase(),
      center: p.get("center") || "",
    };
  }, [search]);

  const label = ({
    suspended:  "Suspended",
    canceled:   "Canceled",
    cancelled:  "Cancelled",
    inactive:   "Deactivated",
  })[status] || "Suspended";

  return (
    <div
      data-testid="suspended-page"
      className="min-h-screen bg-stone-950 text-stone-100 grid place-items-center px-6 py-16"
    >
      <div className="max-w-lg w-full rounded-3xl border border-stone-800 bg-stone-900/60 backdrop-blur p-8 sm:p-10 space-y-5 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-red-500/15 text-red-400 grid place-items-center">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Account {label}</div>
            <h1 className="font-display text-3xl text-white leading-tight">Access has been paused</h1>
          </div>
        </div>

        <p className="text-stone-300 text-sm leading-relaxed">
          {center ? <><b>{center}</b> — this ChildCheck Ohio account has been {label.toLowerCase()} by our team. </> : <>This ChildCheck Ohio account has been {label.toLowerCase()}. </>}
          Every user attached to this center (owners, staff, and parents) has been signed out and can no longer access the dashboard, exports, messaging, or the mobile app until the account is restored.
        </p>

        <div className="rounded-2xl border border-stone-800 bg-stone-950/60 p-5 text-sm space-y-2">
          <div className="text-stone-400 text-[11px] uppercase tracking-widest font-semibold">To restore access</div>
          <div className="text-stone-200">
            Email <a
              href="mailto:support@childcheckoh.com"
              className="text-emerald-400 font-semibold underline underline-offset-4 decoration-emerald-700 hover:text-emerald-300"
              data-testid="suspended-support-link"
            >support@childcheckoh.com</a> and reference your center name. Most reactivation requests are resolved within one business day.
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
          <a
            data-testid="suspended-email-btn"
            href={`mailto:support@childcheckoh.com?subject=Reactivate%20${encodeURIComponent(center || "my center")}`}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition"
          >
            <Mail className="w-4 h-4" /> Email support
          </a>
          <Link
            data-testid="suspended-back-home-link"
            to="/"
            className="w-full sm:w-auto inline-flex items-center justify-center h-11 px-5 rounded-xl border border-stone-700 hover:border-stone-500 text-stone-200 text-sm font-semibold transition"
          >
            Back to childcheckoh.com
          </Link>
        </div>

        <p className="text-[11px] text-stone-500 pt-2">
          If you believe this is a mistake — for example a billing hold that should have cleared — please include the last 4 digits of the card on file and your center license number in your email so we can locate your account fast.
        </p>
      </div>
    </div>
  );
}
